// Caseload by collector — a snapshot of today's worklist per handler; every number clicks through to the table.
// "Activity" tab (Dean, 3 Nov 2026): the collector scoreboard — calls, SMS, emails, promises, arrears, cash per collector.
import { useEffect, useState } from "react";
import { Activity, Download, Loader2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/regionFmt";
import { getAgeingCaseload, downloadAgeingCaseloadCsv } from "@/lib/api";
import { CollectorScoreboard } from "./CollectorScoreboard";

const METRICS = [
  { key: "files", label: "Files", kind: "handler" },
  { key: "arrears", label: "Arrears", kind: "handler", money: true },
  { key: "balance", label: "Total balance", kind: "handler", money: true },
  { key: "overdue_actions", label: "Overdue actions", kind: "ids", tone: "text-red-700" },
  { key: "broken_promises", label: "Broken promises", kind: "ids", tone: "text-rose-700" },
  { key: "promises_due_week", label: "Promises due this week", kind: "ids", tone: "text-indigo-700" },
  { key: "no_contact_14d", label: "No contact 14d", kind: "ids", tone: "text-amber-800" },
  { key: "on_hold", label: "On hold", kind: "none" },
];

export function CaseloadDialog({ open, onOpenChange, onPick, holdLabel = "Breathing Space" }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("worklist");
  const [period, setPeriod] = useState("today");

  useEffect(() => {
    if (!open) return;
    setBusy(true);
    getAgeingCaseload().then(setData).catch((e) => toast.error("Caseload: " + (e?.response?.data?.detail || e.message))).finally(() => setBusy(false));
  }, [open]);

  const csv = async () => {
    try {
      const blob = await downloadAgeingCaseloadCsv();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `caseload_${data?.as_of || new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error("CSV failed: " + (e?.response?.data?.detail || e.message)); }
  };

  const pick = (row, m) => {
    if (m.kind === "none") return;
    onPick?.({ handler: row.handler, metric: m.kind === "ids" ? m.key : null, label: m.label, caseIds: m.kind === "ids" ? row.case_ids?.[m.key] || [] : null, count: row[m.key] });
    onOpenChange(false);
  };

  const Cell = ({ row, m, bold }) => {
    const v = row[m.key];
    const txt = m.money ? fmtMoney(v || 0, 0) : (v || 0).toLocaleString();
    const clickable = m.kind !== "none" && v > 0 && row.handler !== "Total";
    return (
      <td className={`px-2 py-1.5 text-right tabular-nums ${bold ? "font-semibold" : ""}`}>
        {clickable ? (
          <button type="button" onClick={() => pick(row, m)} className={`underline-offset-2 hover:underline ${m.tone || "text-slate-800"}`}
            data-testid={`caseload-${m.key}-${row.handler.replace(/\s+/g, "-").toLowerCase()}`} title={`Open the ageing table filtered to ${row.handler} · ${m.label}`}>
            {txt}
          </button>
        ) : <span className={v ? (m.tone || "") : "text-slate-400"}>{txt}</span>}
      </td>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={tab === "activity" ? "sm:max-w-[96vw] xl:max-w-7xl" : "sm:max-w-4xl"} data-testid="caseload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="w-4 h-4 text-amber-600" /> Caseload by collector</DialogTitle>
          <DialogDescription className="text-xs">
            {tab === "worklist"
              ? <>Today&apos;s arrears worklist per account handler{data ? ` · as of ${data.as_of} · promises due ${data.week_start} → ${data.week_end}` : ""}. Click a number to open the table filtered to those files.</>
              : <>How each collector is doing — calls made and answered, talk time, SMS, emails, promises taken / kept / broken, cash collected on their files, arrears book and movement, average DPD.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="inline-flex rounded-md border bg-white p-0.5 text-xs self-start" data-testid="caseload-tabs">
          <button type="button" onClick={() => setTab("worklist")} className={`px-3 py-1 rounded inline-flex items-center gap-1.5 ${tab === "worklist" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid="caseload-tab-worklist"><Users className="w-3.5 h-3.5" />Worklist</button>
          <button type="button" onClick={() => setTab("activity")} className={`px-3 py-1 rounded inline-flex items-center gap-1.5 ${tab === "activity" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid="caseload-tab-activity"><Activity className="w-3.5 h-3.5" />Activity</button>
        </div>
        {tab === "activity" ? <CollectorScoreboard period={period} onPeriod={setPeriod} /> : (
        <>
        {busy && !data ? <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></div> : data && (
          <div className="overflow-auto max-h-[60vh] border rounded-md">
            <table className="w-full text-xs border-collapse" data-testid="caseload-table">
              <thead className="bg-[#f5f6f8] text-[11px] text-slate-600 sticky top-0">
                <tr className="border-b border-slate-300">
                  <th className="text-left px-2 py-2 font-semibold">Collector</th>
                  {METRICS.map((m) => <th key={m.key} className="text-right px-2 py-2 font-semibold whitespace-nowrap">{m.key === "on_hold" ? holdLabel : m.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.handler} className={`border-b border-slate-200 hover:bg-[#f0f4ff] ${r.handler === "Unassigned" ? "bg-amber-50/40" : "bg-white"} ${r.files === 0 ? "text-slate-400" : ""}`} data-testid={`caseload-row-${r.handler.replace(/\s+/g, "-").toLowerCase()}`}>
                    <td className="px-2 py-1.5 font-medium">{r.handler}</td>
                    {METRICS.map((m) => <Cell key={m.key} row={r} m={m} />)}
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                <tr data-testid="caseload-total">
                  <td className="px-2 py-2 font-semibold uppercase text-[10px] tracking-wider text-slate-500">Total</td>
                  {METRICS.map((m) => <Cell key={m.key} row={data.total} m={m} bold />)}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <div className="flex justify-end">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={csv} disabled={!data} data-testid="caseload-csv">
            <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
          </Button>
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
